import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { SkeletonList } from '@/components/ui/Skeleton'
import { useGames } from '../hooks/useGames'

/**
 * Catalogue des mécaniques jouables (SPEC §12, `GET /practice/games`).
 *
 * N'affiche que ce que le serveur renvoie déjà comme jouable — items
 * disponibles ET mécanique implémentée (`GetPlayableGamesQuery`) — jamais
 * recomposé côté client. Le nom et la description de chaque mécanique
 * viennent des traductions par mécanique, sous `practice.games.<id>`
 * (`i18n/locales/games/{fr,en}/<id>.json`, agrégées dans `i18n/index.ts`).
 */
export function GameCatalog() {
  const { t } = useTranslation()
  const { data: games, isLoading, isError } = useGames()

  return (
    <section aria-labelledby="practice-catalog-title" className="flex flex-col gap-4">
      <h2 id="practice-catalog-title" className="font-display text-lg font-semibold text-on-surface">
        {t('practice.catalog.title')}
      </h2>

      {isError && <Alert variant="error">{t('practice.catalog.load_error')}</Alert>}

      {isLoading && <SkeletonList rows={4} rowClassName="h-24" />}

      {games && games.length === 0 && (
        <p className="rounded-2xl bg-surface-low px-4 py-6 text-center text-sm text-on-surface-variant">
          {t('practice.catalog.empty')}
          <span className="mt-1 block">{t('practice.catalog.empty_hint')}</span>
        </p>
      )}

      {games && games.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {games.map((game) => (
            <li key={game.id}>
              <Link
                to={`/jeux/${game.id}`}
                className="flex min-h-[44px] flex-col gap-1 rounded-2xl bg-surface-low p-4 transition-colors hover:bg-surface-container"
              >
                <span className="font-display text-base font-semibold text-on-surface">
                  {t(`practice.games.${game.id}.name`)}
                </span>
                <span className="text-sm text-on-surface-variant">
                  {t(`practice.games.${game.id}.description`)}
                </span>
                <span className="mt-1 text-xs font-medium uppercase tracking-wide text-primary">
                  {game.mode === 'chrono' ? t('practice.catalog.mode_chrono') : t('practice.catalog.mode_manches')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
